import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DropdownProps {
    value: string
    onChange: (value: string) => void
    options: { value: string; label: string }[]
    className?: string
}

export default function Dropdown({ value, onChange, options, className }: DropdownProps) {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <div className={cn('relative', className)} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center px-3 sm:px-4 py-2 text-sm rounded-lg bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:text-white"
            >
                {value ? value : 'Select'}
                <ChevronDown className="ml-2 h-4 w-4" />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="absolute left-0 mt-2 w-48 rounded-lg bg-zinc-900 border border-zinc-700 shadow-lg py-1 overflow-hidden z-[9999]"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        {options.map((option) => (
                            <button
                                key={option.value}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange(option.value)
                                    setIsOpen(false)
                                }}
                                className="w-full px-4 py-2 text-left text-sm font-light tracking-wider whitespace-nowrap hover:bg-zinc-800/50 text-zinc-400 hover:text-white"
                            >
                                {option.label}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
} 